import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  LinearProgress,
  Alert,
} from '@mui/material';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import TakeTest, { Question } from '../Tests/TakeTest';
import { learnerService, LearnerMastery, CachedItem } from '../../services/learnerService';
import { generateItemsForSkill } from '../../services/practiceItemGenerationService';
import { getAiBackendUrl } from '../../constants/api';
import { buildAiAuthHeaders, consumeSSEStream } from '../../hooks/useSSEStream';
import { effectiveStatus } from '../../utils/masteryStatus';
import OverrideControl from './OverrideControl';
import EvidenceDrawer from './EvidenceDrawer';

const STATUS_LABEL: Record<string, string> = {
  'not-attempted': 'Not started yet',
  emerging: 'Just getting started',
  developing: 'Making progress',
  secure: 'Looks solid - let’s recheck in two weeks.',
};

const STATUS_COLOR: Record<string, 'default' | 'warning' | 'info' | 'success'> = {
  'not-attempted': 'default',
  emerging: 'warning',
  developing: 'info',
  secure: 'success',
};

function cachedItemToQuestion(item: CachedItem): Question {
  return {
    _id: item._id,
    question: item.question,
    answers: item.answers,
    correctAnswer: item.correctAnswer,
    explanation: item.explanation,
    standardRefId: item.standardRefId,
    bloomsLevel: item.bloomsLevel,
    answerType: item.answerType,
  };
}

/** Teach tab: streams a child-facing explanation directly from ai_backend. */
const TeachPanel: React.FC<{ learnerId: string; skillId: string }> = ({ learnerId, skillId }) => {
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token') || '';

    const run = async () => {
      setLoading(true);
      setError(null);
      setText('');
      try {
        const session = await learnerService.startTeachSession(learnerId, skillId, token);
        if (cancelled) return;

        await consumeSSEStream({
          url: `${getAiBackendUrl()}/instruction/explain-skill`,
          headers: buildAiAuthHeaders(),
          body: JSON.stringify({
            skillCode: session.skill.code,
            skillName: session.skill.name,
            skillDescription: session.skill.description,
            gradeLevel: session.skill.gradeLevel,
            standards: session.skill.standards,
          }),
          onEvent: (data) => {
            if (cancelled) return;
            if (typeof data.content === 'string') {
              setText((prev) => prev + data.content);
            }
          },
        });
      } catch (err) {
        console.error('Error streaming skill explanation:', err);
        if (!cancelled) setError('Unable to load the explanation right now. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [learnerId, skillId]);

  return (
    <Paper sx={{ p: 3 }}>
      {error && <Alert severity="warning">{error}</Alert>}
      {loading && !text && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">Preparing an explanation...</Typography>
        </Box>
      )}
      {text && (
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
          {text}
        </Typography>
      )}
    </Paper>
  );
};

type ItemFlowStep = 'loading' | 'generating' | 'taking' | 'submitting' | 'done' | 'error';

/** Shared implementation for the Practice and Recheck tabs - identical flow,
 *  different endpoints (see db_backend/api/{practice,recheck}.js). */
const PracticeOrRecheckPanel: React.FC<{
  learnerId: string;
  skillId: string;
  skillName: string;
  gradeLevel: number;
  variant: 'practice' | 'recheck';
  onMasteryChange: () => void;
}> = ({ learnerId, skillId, skillName, gradeLevel, variant, onMasteryChange }) => {
  const [step, setStep] = useState<ItemFlowStep>('loading');
  const [items, setItems] = useState<Question[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [resultStatus, setResultStatus] = useState<string | null>(null);
  const token = localStorage.getItem('token') || '';

  const getItems = variant === 'practice' ? learnerService.getPracticeItems : learnerService.getRecheckItems;
  const submitSkillItems = variant === 'practice' ? learnerService.submitPracticeSkillItems : learnerService.submitRecheckSkillItems;
  const submitAnswers = variant === 'practice' ? learnerService.submitPractice : learnerService.submitRecheck;

  const load = useCallback(async () => {
    setStep('loading');
    setError(null);
    try {
      const res = await getItems(learnerId, skillId, token, 3);
      if (!res.needsGeneration && res.items.length > 0) {
        setItems(res.items.map(cachedItemToQuestion));
        setStep('taking');
        return;
      }

      setStep('generating');
      const generated = await generateItemsForSkill(
        { code: res.skillCode, name: res.skillName, gradeLevel: res.gradeLevel || gradeLevel, standards: [] },
        3
      );
      if (generated.length === 0) {
        setError('Unable to prepare items right now. Please try again.');
        setStep('error');
        return;
      }

      const validated = await submitSkillItems(learnerId, skillId, generated, token);
      if (validated.items.length === 0) {
        setError('Unable to prepare valid items right now. Please try again.');
        setStep('error');
        return;
      }
      setItems(validated.items.map(cachedItemToQuestion));
      setStep('taking');
    } catch (err) {
      console.error(`Error loading ${variant} items:`, err);
      setError('Something went wrong. Please try again.');
      setStep('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learnerId, skillId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmitComplete = async (answers: Record<string, string>) => {
    setStep('submitting');
    try {
      const res = await submitAnswers(learnerId, skillId, answers, token);
      setResultStatus(res.mastery.status);
      setStep('done');
      onMasteryChange();
    } catch (err) {
      console.error(`Error submitting ${variant}:`, err);
      setError('Something went wrong submitting your answers. Please try again.');
      setStep('error');
    }
  };

  if (step === 'loading' || step === 'generating') {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          {step === 'generating' ? `Preparing ${skillName} questions...` : 'Loading...'}
        </Typography>
      </Paper>
    );
  }

  if (step === 'error') {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="contained" onClick={load}>Try Again</Button>
      </Paper>
    );
  }

  if (step === 'submitting') {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body2">Checking your answers...</Typography>
      </Paper>
    );
  }

  if (step === 'done') {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Nice work!</Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {resultStatus ? STATUS_LABEL[resultStatus] : 'Your answers have been recorded.'}
        </Typography>
        <Button variant="outlined" onClick={load}>Do More</Button>
      </Paper>
    );
  }

  // taking
  return (
    <TakeTest
      mode={variant}
      items={items}
      itemsTitle={`${variant === 'practice' ? 'Practice' : 'Recheck'}: ${skillName}`}
      onSubmitComplete={handleSubmitComplete}
    />
  );
};

const SkillDetail: React.FC = () => {
  const { skillId } = useParams<{ skillId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const learnerId = searchParams.get('learnerId');

  const [tab, setTab] = useState<'teach' | 'practice' | 'recheck'>('teach');
  const [skillInfo, setSkillInfo] = useState<{ name: string; description: string; gradeLevel: number } | null>(null);
  const [mastery, setMastery] = useState<LearnerMastery | null>(null);
  const [loading, setLoading] = useState(true);
  const [evidenceDrawerOpen, setEvidenceDrawerOpen] = useState(false);

  const loadMastery = useCallback(async () => {
    if (!learnerId || !skillId) return;
    const token = localStorage.getItem('token') || '';
    try {
      const data = await learnerService.getSkillMastery(learnerId, skillId, token);
      setMastery(data.mastery ?? null);
    } catch (err) {
      console.warn('No mastery yet for this skill:', err);
      setMastery(null);
    }
  }, [learnerId, skillId]);

  useEffect(() => {
    if (!skillId) return;
    const token = localStorage.getItem('token') || '';
    setLoading(true);
    learnerService
      .getSkill(skillId, token)
      .then((data) => {
        setSkillInfo({
          name: data.skill.name,
          description: data.skill.description,
          gradeLevel: data.skill.gradeLevel,
        });
      })
      .catch((err) => console.error('Error fetching skill:', err))
      .finally(() => setLoading(false));
    loadMastery();
  }, [skillId, loadMastery]);

  if (!learnerId || !skillId) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error">Missing learner or skill.</Alert>
      </Container>
    );
  }

  if (loading || !skillInfo) {
    return (
      <Container maxWidth="md" sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const status = effectiveStatus(mastery);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button variant="text" onClick={() => navigate('/dashboard')} sx={{ mb: 2 }}>
        &larr; Back to Skill Map
      </Button>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{skillInfo.name}</Typography>
          <Chip label={STATUS_LABEL[status]} color={STATUS_COLOR[status]} />
        </Box>
        {mastery && mastery.evidenceCount > 0 && (
          <Typography variant="body2" color="text.secondary">
            {mastery.correctCount} of {mastery.evidenceCount} correct ({Math.round(mastery.confidence * 100)}% confidence)
          </Typography>
        )}
        {mastery?.parentOverride && (
          <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }} color="text.secondary">
            You set this status{mastery.parentOverride.note ? `: "${mastery.parentOverride.note}"` : ''}
          </Typography>
        )}
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" onClick={() => setEvidenceDrawerOpen(true)}>
            See the evidence
          </Button>
          <OverrideControl
            learnerId={learnerId}
            skillId={skillId}
            currentEffectiveStatus={status}
            existingOverride={mastery?.parentOverride}
            onOverridden={loadMastery}
          />
        </Box>
      </Paper>

      <EvidenceDrawer
        open={evidenceDrawerOpen}
        onClose={() => setEvidenceDrawerOpen(false)}
        learnerId={learnerId}
        skillId={skillId}
        skillName={skillInfo.name}
      />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Teach" value="teach" />
        <Tab label="Practice" value="practice" />
        <Tab label="Recheck" value="recheck" />
      </Tabs>

      {tab === 'teach' && <TeachPanel learnerId={learnerId} skillId={skillId} />}
      {tab === 'practice' && (
        <PracticeOrRecheckPanel
          learnerId={learnerId}
          skillId={skillId}
          skillName={skillInfo.name}
          gradeLevel={skillInfo.gradeLevel}
          variant="practice"
          onMasteryChange={loadMastery}
        />
      )}
      {tab === 'recheck' && (
        <PracticeOrRecheckPanel
          learnerId={learnerId}
          skillId={skillId}
          skillName={skillInfo.name}
          gradeLevel={skillInfo.gradeLevel}
          variant="recheck"
          onMasteryChange={loadMastery}
        />
      )}
    </Container>
  );
};

export default SkillDetail;
